import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { getSuppliers } from '@/api/providersAPI'
import APIServicesV2 from '@/api/APIServicesV2'
import type { SearchableSelectExpandedOption } from '@/components/newRedesign/components/filters'

// ==================== SUPPLIERS ====================

/**
 * Fetcher for suppliers compatible with SearchableSelect
 */
export function useSuppliersFetcher() {
  const queryClient = useQueryClient()

  return useCallback(
    async (search: string): Promise<SearchableSelectExpandedOption[]> => {
      const queryKey = ['suppliers', search]
      const cached =
        queryClient.getQueryData<SearchableSelectExpandedOption[]>(queryKey)

      if (cached) {
        return cached
      }

      const suppliers = await getSuppliers({
        page: 1,
        per_page: 50,
        search: search.trim() || undefined,
      })

      // getSuppliers retorna directamente el array, no un objeto con items
      const mappedResult = (Array.isArray(suppliers) ? suppliers : []).map(
        (supplier: { id: number; name: string }) => ({
          id: supplier.id,
          label: supplier.name || 'Sin nombre',
        })
      )

      queryClient.setQueryData(queryKey, mappedResult, {
        updatedAt: Date.now(),
      })

      return mappedResult
    },
    [queryClient]
  )
}

// ==================== CONTAINERS ====================

/**
 * Fetcher for containers compatible with SearchableSelect
 */
export function useContainersFetcher() {
  const queryClient = useQueryClient()

  return useCallback(
    async (search: string): Promise<SearchableSelectExpandedOption[]> => {
      const queryKey = ['containers', search]
      const cached =
        queryClient.getQueryData<SearchableSelectExpandedOption[]>(queryKey)

      if (cached) {
        return cached
      }

      const cleanParams = {
        page: 1,
        per_page: 50,
        ...(search.trim() && { search: search.trim() }),
      }

      const response = await APIServicesV2.get('/containers', {
        params: cleanParams,
      })

      const containers = response.data?.items || []

      const mappedResult = containers.map(
        (container: { id: number; name: string }) => ({
          id: container.id,
          label: container.name || `Contenedor #${container.id}`,
        })
      )

      queryClient.setQueryData(queryKey, mappedResult, {
        updatedAt: Date.now(),
      })

      return mappedResult
    },
    [queryClient]
  )
}
